import React, { useCallback, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Card from "../../ui/Card";
import { useTheme } from "../../../contexts/ThemeContext";
import { hexToRgba } from "../../../utils/themes";

const LEGEND_SERIES = [
  { dataKey: "match_count", label: "Overall match" },
  { dataKey: "company_count", label: "Company match" },
  { dataKey: "role_count", label: "Role match" },
];

function opacityForSeries(dataKey, hoveredKey, selectedKeys) {
  if (hoveredKey && hoveredKey !== dataKey) return 0.25;
  if (hoveredKey === dataKey) return 1;
  if (selectedKeys.size > 0 && !selectedKeys.has(dataKey)) return 0.25;
  return 1;
}

function ScoreHistogramLegend({ accent, hoveredKey, selectedKeys, onHover, onLeave, onToggle }) {
  const companyFill = hexToRgba(accent, 0.58);
  const roleFill = hexToRgba(accent, 0.3);
  const fills = {
    match_count: accent,
    company_count: companyFill,
    role_count: roleFill,
  };
  return (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 font-mono text-[10px] text-theme-text mt-1 px-1">
      {LEGEND_SERIES.map(({ dataKey, label }) => {
        const sel = selectedKeys.has(dataKey);
        const o = opacityForSeries(dataKey, hoveredKey, selectedKeys);
        return (
          <li key={dataKey}>
            <button
              type="button"
              className={`flex items-center gap-1.5 shrink-0 rounded px-1 py-0.5 cursor-pointer border border-transparent transition-opacity duration-150 ${
                sel ? "border-theme-secondary" : ""
              }`}
              style={{ opacity: o }}
              aria-pressed={sel}
              onMouseEnter={() => onHover(dataKey)}
              onMouseLeave={onLeave}
              onClick={() => onToggle(dataKey)}
            >
              <span className="shrink-0 w-2 h-2 rounded-[2px] pointer-events-none" style={{ background: fills[dataKey] }} />
              <span className="pointer-events-none">{label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function AuditHistogramChart({ histogram }) {
  const { theme } = useTheme();
  const rows = histogram || [];
  const accent = theme.accent;
  const grid = hexToRgba(theme.border, 0.35);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  const toggleSeries = useCallback((dataKey) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) next.delete(dataKey);
      else next.add(dataKey);
      return next;
    });
  }, []);

  const leaveLegend = useCallback(() => setHoveredKey(null), []);

  return (
    <Card className="p-4 md:p-5 h-full flex flex-col">
      <h3 className="font-display font-bold italic text-lg text-theme-on-background mb-1">Fit score distribution</h3>
      <p className="font-mono text-[10px] text-theme-dim mb-2 leading-snug">
        Overall fit, company fit, and role fit across scored cold outreach.
      </p>
      <div className="h-[220px] w-full flex-1 min-h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 4, left: 0, bottom: 4 }} barCategoryGap="10%">
            <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: theme.dim, fontSize: 9, fontFamily: "monospace" }}
              tickLine={{ stroke: theme.border }}
              axisLine={{ stroke: theme.border }}
              interval={0}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: theme.dim, fontSize: 11, fontFamily: "monospace" }}
              width={36}
            />
            <Tooltip
              labelFormatter={(tick) => `Scores toward ${tick}`}
              contentStyle={{
                background: theme.panel,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                fontSize: 12,
                color: theme.text,
              }}
              labelStyle={{ color: theme.dim }}
            />
            <Bar
              dataKey="match_count"
              name="Overall match"
              fill={accent}
              radius={[3, 3, 0, 0]}
              fillOpacity={opacityForSeries("match_count", hoveredKey, selectedKeys)}
              isAnimationActive={false}
            />
            <Bar
              dataKey="company_count"
              name="Company match"
              fill={hexToRgba(accent, 0.58)}
              radius={[3, 3, 0, 0]}
              fillOpacity={opacityForSeries("company_count", hoveredKey, selectedKeys)}
              isAnimationActive={false}
            />
            <Bar
              dataKey="role_count"
              name="Role match"
              fill={hexToRgba(accent, 0.3)}
              radius={[3, 3, 0, 0]}
              fillOpacity={opacityForSeries("role_count", hoveredKey, selectedKeys)}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ScoreHistogramLegend
        accent={accent}
        hoveredKey={hoveredKey}
        selectedKeys={selectedKeys}
        onHover={setHoveredKey}
        onLeave={leaveLegend}
        onToggle={toggleSeries}
      />
    </Card>
  );
}
